import React, { useEffect, useRef, useState } from "react";
import { Modal, Table, Input, Select, Button, Tooltip, Typography, message } from "antd";
import { PlusOutlined, DeleteOutlined, SettingOutlined, UploadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import NumberInput from "@/components/common/NumberInput";
import { COLORS } from "@/utils/constants";
import {
  hasUnitConversionPlaceholder,
  replaceUnitConversionPlaceholder,
  type PreviewCategoryConfig,
  type PreviewColumnConfig,
  type SectionConfig,
} from "./types";
import DynamicColumnsConfigModal, { type DynamicColumnUpdate } from "./DynamicColumnsConfigModal";
import { importTakeoffFromExcel, type ImportedTakeoffRow } from "./importTakeoffUtils";

const { Text } = Typography;

interface DemoRow {
  id: string;
  values: Record<string, string | number>;
}

interface FlatColumn {
  colKey: string;
  col: PreviewColumnConfig;
}

let demoRowIdCounter = 0;
const nextDemoRowId = () => `demo-row-${++demoRowIdCounter}`;

// A dynamic column with configured headers fans out into one physical column
// per configured header value (e.g. "Run 1", "Run 2", "Run 3") instead of a
// single generic column. Shared between rendering and Excel import so both
// see the exact same column-to-colKey layout for a given category.
function buildFlatColumnGroups(
  category: PreviewCategoryConfig,
): { subCategory: string; flats: FlatColumn[] }[] {
  let counter = 0;
  return category.subCategories.map((subCat) => {
    const flats: FlatColumn[] = [];
    subCat.tableColumns.forEach((col) => {
      if (col.isDynamic && col.options && col.options.length > 0) {
        col.options.forEach((headerValue) => {
          flats.push({ colKey: `c${counter++}`, col: { ...col, tableColumn: headerValue } });
        });
      } else {
        flats.push({ colKey: `c${counter++}`, col });
      }
    });
    return { subCategory: subCat.subCategory, flats };
  });
}

// The Table Column name stays as the original template (e.g. "depth n.e.
// xxxmm") so Configure Headers can keep showing/editing it — this resolves
// the actual text to display in the rendered preview table header. 0 is the
// default/unset sentinel (see the Unit Conversion switch's off-reset), so a
// value hasn't actually been configured yet — keep showing the placeholder
// itself rather than substituting a literal "0".
function resolveTableColumn(col: PreviewColumnConfig): string {
  return col.unitConversion && col.unitConversionValue !== 0 && hasUnitConversionPlaceholder(col.tableColumn)
    ? replaceUnitConversionPlaceholder(col.tableColumn, col.unitConversionValue)
    : col.tableColumn;
}

// Sections like Drainage (Supplier: OSMA vs POLYPIPE) or Plot Drives (Drive
// Stage: Drives to Binder vs Drives Surface Course) carry a variant per row,
// each with its own set of categories — collects the distinct variant values
// present so the preview can offer a switcher.
function getVariantOptions(config: SectionConfig): string[] {
  const variants: string[] = [];
  config.categories.forEach((cat) => {
    cat.subCategories.forEach((sub) => {
      sub.tableColumns.forEach((col) => {
        if (col.variant && !variants.includes(col.variant)) {
          variants.push(col.variant);
        }
      });
    });
  });
  return variants;
}

// Excel cells are read with raw:false (see importTakeoffUtils.ts), so every
// imported value arrives as a string regardless of the column's configured
// field type. Number columns need an actual number — the render path below
// only recognises typeof "number" and otherwise falls back to 0 — and
// Dropdown columns need to match one of their configured options despite
// incidental whitespace/case differences from the source spreadsheet.
function coerceImportedValue(
  value: string | number | null | undefined,
  col: PreviewColumnConfig,
): string | number {
  if (value === null || value === undefined || value === "") {
    return col.fieldType === "Number" ? 0 : "";
  }
  if (col.fieldType === "Number") {
    const numeric = typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""));
    return Number.isNaN(numeric) ? 0 : numeric;
  }
  const text = String(value).trim();
  if (col.fieldType === "Dropdown" && col.options) {
    const match = col.options.find((option) => option.toLowerCase() === text.toLowerCase());
    if (match) return match;
  }
  return text;
}

interface TablePreviewModalProps {
  open: boolean;
  config: SectionConfig | null;
  onClose: () => void;
  onSaveDynamicOptions?: (updates: DynamicColumnUpdate[]) => void;
}

const TablePreviewModal: React.FC<TablePreviewModalProps> = ({
  open,
  config,
  onClose,
  onSaveDynamicOptions,
}) => {
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [rowsByCategory, setRowsByCategory] = useState<Record<string, DemoRow[]>>({});
  const [variantFilter, setVariantFilter] = useState<string>("");
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Deliberately keyed on config?.section rather than the whole config
  // object: config is rebuilt (new reference) on every sectionRows edit,
  // including the unitConversionValue update Import Takeoff applies after a
  // successful import — keying on the full object would wipe the
  // just-imported rows right back out via this same reset.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && config) {
      setActiveCategory(config.categories[0]?.category ?? "");
      setRowsByCategory({});
      setVariantFilter("");
    }
  }, [open, config?.section]);

  useEffect(() => {
    setConfigModalOpen(false);
  }, [activeCategory]);

  if (!config) return null;

  const variantOptions = getVariantOptions(config);
  const hasVariants = variantOptions.length > 0;
  const effectiveVariantFilter = hasVariants ? variantFilter || variantOptions[0] : "";

  // Only the categories belonging to the selected variant (Supplier, Drive
  // Stage, etc.) are shown as tabs.
  const visibleCategories = hasVariants
    ? config.categories.filter((cat) =>
        cat.subCategories.some((sub) => sub.tableColumns.some((col) => col.variant === effectiveVariantFilter)),
      )
    : config.categories;

  const effectiveActiveCategory = visibleCategories.some((cat) => cat.category === activeCategory)
    ? activeCategory
    : visibleCategories[0]?.category ?? "";

  const activeCategoryConfig = visibleCategories.find((cat) => cat.category === effectiveActiveCategory);
  const subCategories = activeCategoryConfig?.subCategories ?? [];
  const demoRows = rowsByCategory[effectiveActiveCategory] ?? [];

  const subCategoryGroups = activeCategoryConfig ? buildFlatColumnGroups(activeCategoryConfig) : [];
  const allFlatColumns: FlatColumn[] = subCategoryGroups.flatMap((group) => group.flats);

  // Configure Headers operates on the original (un-fanned) columns across the
  // WHOLE section (every category/sub-category, not just the active tab) —
  // one entry per row that either fans out into multiple headers (Is Dynamic)
  // or still has an "xxx"-style placeholder to substitute (Unit Conversion).
  // Scoping to only the active tab would hide rows sitting under a category
  // the user isn't currently viewing (e.g. a freshly added row with no
  // Category set yet, which defaults to "Uncategorized").
  const dynamicColumns = config.categories
    .flatMap((cat) => cat.subCategories)
    .flatMap((subCat) => subCat.tableColumns)
    .filter((col) => col.isDynamic || (col.unitConversion && hasUnitConversionPlaceholder(col.tableColumn)))
    .map((col, idx) => ({
      rowId: col.rowId,
      label: col.tableColumn || `Column ${idx + 1}`,
      options: col.options ?? [],
      isDynamic: col.isDynamic,
      hasUnitConversion: col.unitConversion && hasUnitConversionPlaceholder(col.tableColumn),
      unitConversionValue: col.unitConversionValue,
    }));

  const addDemoRow = () => {
    if (allFlatColumns.length === 0) return;
    const values: Record<string, string | number> = {};
    allFlatColumns.forEach(({ colKey, col }) => {
      values[colKey] = col.fieldType === "Number" ? 0 : "";
    });
    setRowsByCategory((prev) => ({
      ...prev,
      [effectiveActiveCategory]: [...(prev[effectiveActiveCategory] ?? []), { id: nextDemoRowId(), values }],
    }));
  };

  const removeDemoRow = (rowId: string) => {
    setRowsByCategory((prev) => ({
      ...prev,
      [effectiveActiveCategory]: (prev[effectiveActiveCategory] ?? []).filter((row) => row.id !== rowId),
    }));
  };

  const updateDemoRow = (rowId: string, colKey: string, value: string | number) => {
    setRowsByCategory((prev) => ({
      ...prev,
      [effectiveActiveCategory]: (prev[effectiveActiveCategory] ?? []).map((row) =>
        row.id === rowId ? { ...row, values: { ...row.values, [colKey]: value } } : row,
      ),
    }));
  };

  const handleImportFileSelected = async (file: File) => {
    setImporting(true);
    try {
      const { rows: importedRows, errors, unitConversionValues } = await importTakeoffFromExcel(file, config);
      if (errors.length > 0) {
        message.error(errors[0]);
        return;
      }
      if (importedRows.length === 0) {
        message.warning("No data rows were found to import.");
        return;
      }

      if (unitConversionValues.length > 0 && onSaveDynamicOptions) {
        onSaveDynamicOptions(
          unitConversionValues.map(({ rowId, value }) => ({
            rowId,
            options: dynamicColumns.find((col) => col.rowId === rowId)?.options ?? [],
            unitConversionValue: value,
          })),
        );
      }

      const nextRowsByCategory: Record<string, DemoRow[]> = {};
      config.categories.forEach((category) => {
        const categoryFlats = buildFlatColumnGroups(category).flatMap((group) => group.flats);
        nextRowsByCategory[category.category] = importedRows.map(
          (importedRow: ImportedTakeoffRow, index: number) => {
            const values: Record<string, string | number> = {};
            categoryFlats.forEach(({ colKey, col }) => {
              const value = importedRow[col.rowId];
              values[colKey] = coerceImportedValue(value, col);
            });
            return { id: `import-${category.category}-${index}`, values };
          },
        );
      });

      setRowsByCategory(nextRowsByCategory);
      message.success(`Imported ${importedRows.length} row${importedRows.length === 1 ? "" : "s"}.`);
    } catch {
      message.error("Failed to read the uploaded Excel file.");
    } finally {
      setImporting(false);
    }
  };

  const buildLeafColumn = (colKey: string, col: PreviewColumnConfig, idx: number) => ({
    title: (
      <div style={{ textAlign: "center" as const }}>
        <div>{resolveTableColumn(col) || `Column ${idx + 1}`}</div>
        {col.evaluateCode && (
          <div style={{ fontSize: 10, color: "#999", fontWeight: 500, marginTop: 2, lineHeight: 1.3 }}>
            {col.evaluateCode}
          </div>
        )}
      </div>
    ),
    dataIndex: colKey,
    key: colKey,
    width: 180,
    render: (_: unknown, record: DemoRow) => {
      const value = record.values[colKey];
      if (col.fieldType === "Number") {
        return (
          <NumberInput
            value={typeof value === "number" ? value : 0}
            onChange={(v) => updateDemoRow(record.id, colKey, v)}
          />
        );
      }
      if (col.fieldType === "Dropdown") {
        return (
          <Select
            value={(value as string) || undefined}
            placeholder="Select"
            allowClear
            style={{ width: "100%" }}
            options={(col.options ?? []).map((option) => ({ label: option, value: option }))}
            onChange={(v) => updateDemoRow(record.id, colKey, v ?? "")}
          />
        );
      }
      return (
        <Input
          value={(value as string) || ""}
          placeholder={col.takeoffImportName || col.tableColumn || "Value"}
          onChange={(e) => updateDemoRow(record.id, colKey, e.target.value)}
        />
      );
    },
  });

  // Only show a sub-category group header row when there's more than one
  // sub-category, or the single sub-category was explicitly named.
  const showSubCategoryGroups =
    subCategories.length > 1 || (subCategories.length === 1 && subCategories[0].subCategory !== "General");

  let columns: ColumnsType<DemoRow>;
  if (showSubCategoryGroups) {
    columns = subCategoryGroups
      .map(({ subCategory, flats }) => {
        const children = flats.map((flat, idx) => buildLeafColumn(flat.colKey, flat.col, idx));

        if (children.length === 0) return null;

        return {
          title: <div style={{ textAlign: "center" as const, fontWeight: 600 }}>{subCategory}</div>,
          key: `group-${subCategory}`,
          children,
        };
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);
  } else {
    columns = allFlatColumns.map(({ colKey, col }, idx) => buildLeafColumn(colKey, col, idx));
  }

  if (columns.length > 0) {
    columns.push({
      title: "",
      key: "actions",
      width: 50,
      align: "center" as const,
      render: (_: unknown, record: DemoRow) => (
        <Tooltip title="Remove row">
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => removeDemoRow(record.id)}
          />
        </Tooltip>
      ),
    });
  }

  return (
    <Modal
      title={`${config.section} — Table Preview`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={960}
    >
      {config.categories.length === 0 ? (
        <Text type="secondary">No columns configured yet — add rows in this section first.</Text>
      ) : (
        <>
          {hasVariants && (
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}>
              <Select
                value={effectiveVariantFilter}
                onChange={setVariantFilter}
                style={{ width: 240 }}
                size="middle"
                options={variantOptions.map((variant) => ({ label: variant, value: variant }))}
              />
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              backgroundColor: "#fff",
            }}
          >
            {visibleCategories.map((cat) => {
              const active = cat.category === effectiveActiveCategory;
              return (
                <Button
                  key={cat.category}
                  type={active ? "primary" : "default"}
                  onClick={() => setActiveCategory(cat.category)}
                  style={{
                    backgroundColor: active ? COLORS.primary : "#E1E6EF",
                    borderColor: active ? COLORS.primary : "#E1E6EF",
                    color: active ? "#fff" : "#6B7280",
                    fontWeight: 500,
                    borderRadius: "6px 6px 0 0",
                    height: "38px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    borderBottom: active ? "none" : "1px solid #E1E6EF",
                    position: "relative",
                    zIndex: active ? 2 : 1,
                  }}
                >
                  {cat.category}
                </Button>
              );
            })}
            <div style={{ flex: 1 }} />
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) handleImportFileSelected(file);
              }}
            />
            <Button
              icon={<UploadOutlined />}
              loading={importing}
              onClick={() => fileInputRef.current?.click()}
              type="default"
              style={{
                height: "32px",
                borderRadius: "6px",
                fontWeight: 500,
                borderColor: "#1890ff",
                color: "#1890ff",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              Import Takeoff
            </Button>
            {dynamicColumns.length > 0 && (
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModalOpen(true)}
                type="default"
                style={{
                  height: "32px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  borderColor: "#1890ff",
                  color: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                Configure Headers
              </Button>
            )}
          </div>
          <Table<DemoRow>
            dataSource={demoRows}
            columns={columns}
            rowKey="id"
            bordered
            size="middle"
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: 'No demo rows yet — click "Add Row" to try out this table.' }}
          />
          <div style={{ marginTop: 12 }}>
            <Button icon={<PlusOutlined />} onClick={addDemoRow}>
              Add Row
            </Button>
          </div>
        </>
      )}

      <DynamicColumnsConfigModal
        open={configModalOpen}
        columns={dynamicColumns}
        onCancel={() => setConfigModalOpen(false)}
        onSave={(updates) => {
          onSaveDynamicOptions?.(updates);
          setConfigModalOpen(false);
        }}
      />
    </Modal>
  );
};

export default TablePreviewModal;
